import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Track error count to prevent infinite error loops
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Send error to monitoring service (if configured)
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry, LogRocket, etc.
      // this.logErrorToService(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Prevent infinite error loops
      if (this.state.errorCount > 3) {
        return (
          <div className="error-boundary-critical">
            <AlertTriangle size={48} />
            <h1>Critical Error</h1>
            <p>Multiple errors detected. Please reload the page.</p>
            <button onClick={this.handleReload} className="error-button primary">
              <RefreshCw size={16} />
              Reload Page
            </button>
          </div>
        );
      }

      // Custom fallback UI can be passed as prop
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-icon">
              <AlertTriangle size={64} />
            </div>

            <h1 className="error-title">
              {this.props.title || 'Something went wrong'}
            </h1>

            <p className="error-description">
              {this.props.description ||
                "We're sorry, but something unexpected happened. You can try reloading the page or going back to the homepage."}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-stack">
                  <strong>{this.state.error.toString()}</strong>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className="error-actions">
              <button
                onClick={this.handleReset}
                className="error-button secondary"
              >
                <RefreshCw size={16} />
                Try Again
              </button>

              <button
                onClick={this.handleReload}
                className="error-button secondary"
              >
                <RefreshCw size={16} />
                Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                className="error-button primary"
              >
                <Home size={16} />
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
